/*
 Кеш списка тегов.

 Список тегов большой, а дропдаун монтируется заново на каждое открытие, поэтому
 запрашивать его каждый раз с лоадером дорого. Схема такая:
   - первое открытие: запрос с лоадером, ответ кладём в кеш;
   - следующие открытия: сразу показываем кеш, свежий список тянем фоном;
   - пришедший ответ сверяем с кешем порциями (см. idleChunks) и подменяем только
     то, что реально изменилось или появилось.

 Кеш общий на вкладку, чистится при выходе из аккаунта (clearSession).
*/
import { computed, onUnmounted, ref, shallowRef, watch } from "vue";
import type { ComputedRef, Ref, ShallowRef } from "vue";
import { TagsApi } from "@/ api/TagsApi";
import type { TagContainer, TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { TagEntityType } from "@/models/etc/types";
import { runIdleChunks } from "@/utils/idleChunks";

type TagListKey = keyof TagContainer;

const LIST_KEYS: TagListKey[] = ["AllSystemTags", "OftenUsedByUserTags", "OftenUsedForEntityTags"];

/** Совпадают ли теги по всем отображаемым полям. */
function sameTag(prev: TagWithFrequency, next: TagWithFrequency): boolean {
  return prev.Id === next.Id
    && prev.Frequency === next.Frequency
    && prev.Name === next.Name
    && prev.Type === next.Type
    && prev.FullName === next.FullName;
}

/** Переносит поля в существующий объект, сохраняя ссылку на него. */
function applyTag(prev: TagWithFrequency, next: TagWithFrequency): void {
  prev.Name = next.Name;
  prev.Type = next.Type;
  prev.Frequency = next.Frequency;
  prev.FullName = next.FullName;
}

/** Сколько тегов обрабатываем между проверками «есть ли ещё время в слайсе». */
const BATCH = 2000;

const EMPTY_CONTAINER: TagContainer = Object.freeze({
  AllSystemTags: [],
  OftenUsedByUserTags: [],
  OftenUsedForEntityTags: [],
}) as TagContainer;

function emptyContainer(): TagContainer {
  return { AllSystemTags: [], OftenUsedByUserTags: [], OftenUsedForEntityTags: [] };
}

type CacheEntry = {
  container: ShallowRef<TagContainer>;
  loaded: boolean;
  /** сколько селекторов этого типа сейчас смонтировано */
  subscribers: number;
  inflight: Promise<void> | null;
  /** теги поменяли, пока запрос был в полёте, — нужен ещё один заход */
  dirty: boolean;
  /** отмена незавершённой фоновой сверки */
  cancel: (() => void) | null;
};

const cache = new Map<number, CacheEntry>();

function getEntry(type: number): CacheEntry {
  const existing = cache.get(type);
  if (existing) {
    return existing;
  }
  const entry: CacheEntry = {
    container: shallowRef(emptyContainer()),
    loaded: false,
    subscribers: 0,
    inflight: null,
    dirty: false,
    cancel: null,
  };
  cache.set(type, entry);
  return entry;
}

/** Первая загрузка: списки берём как есть, страхуемся только от пустых полей. */
function buildContainer(data: TagContainer): TagContainer {
  return {
    AllSystemTags: data?.AllSystemTags ?? [],
    OftenUsedByUserTags: data?.OftenUsedByUserTags ?? [],
    OftenUsedForEntityTags: data?.OftenUsedForEntityTags ?? [],
  };
}

type MergeMode = "compare" | "index" | "rebuild";

type MergeJob = {
  key: TagListKey;
  prev: TagWithFrequency[];
  next: TagWithFrequency[];
  mode: MergeMode;
  cursor: number;
  byId: Map<number, TagWithFrequency>;
  result: TagWithFrequency[] | null;
};

/**
 * Сверяет свежий ответ с кешем и вливает изменения. Работа идёт порциями в
 * свободное время браузера, промис резолвится по завершении (или при отмене).
 */
function mergeContainer(entry: CacheEntry, data: TagContainer): Promise<void> {
  const prev = entry.container.value;
  const jobs: MergeJob[] = LIST_KEYS.map((key) => ({
    key,
    prev: prev[key],
    next: data?.[key] ?? [],
    mode: "compare" as MergeMode,
    cursor: 0,
    byId: new Map<number, TagWithFrequency>(),
    result: null,
  }));

  return new Promise<void>((resolve) => {
    let jobIndex = 0;
    let changed = false;

    const startRebuild = (job: MergeJob) => {
      job.mode = "index";
      job.cursor = 0;
      job.result = [];
      changed = true;
    };

    const finish = () => {
      entry.cancel = null;
      // ничего не поменялось — контейнер не трогаем, перерисовки не будет
      if (changed) {
        const next = emptyContainer();
        for (const job of jobs) {
          next[job.key] = job.result ?? job.prev;
        }
        entry.container.value = next;
      }
      resolve();
    };

    /** Сверка сломалась — оставляем прежний контейнер, но обязательно отпускаем промис. */
    const abort = (e: unknown) => {
      console.error(e);
      entry.cancel = null;
      resolve();
    };

    const step = (hasTime: () => boolean): boolean => {
      do {
        const job = jobs[jobIndex];
        if (!job) {
          finish();
          return true;
        }

        // 1) быстрый путь: позиционное сравнение, только примитивы и ни одной аллокации
        if (job.mode === "compare") {
          if (job.prev.length !== job.next.length) {
            startRebuild(job);
            continue;
          }
          const end = Math.min(job.cursor + BATCH, job.next.length);
          let mismatch = false;
          while (job.cursor < end) {
            if (!sameTag(job.prev[job.cursor], job.next[job.cursor])) {
              mismatch = true;
              break;
            }
            job.cursor += 1;
          }
          if (mismatch) {
            startRebuild(job);
            continue;
          }
          if (job.cursor >= job.next.length) {
            // список не изменился — оставляем прежний массив по ссылке
            job.result = job.prev;
            jobIndex += 1;
          }
          continue;
        }

        // 2) индексируем прежние теги, чтобы искать их по Id за константу
        if (job.mode === "index") {
          const end = Math.min(job.cursor + BATCH, job.prev.length);
          while (job.cursor < end) {
            const tag = job.prev[job.cursor];
            job.byId.set(tag.Id, tag);
            job.cursor += 1;
          }
          if (job.cursor >= job.prev.length) {
            job.mode = "rebuild";
            job.cursor = 0;
          }
          continue;
        }

        // 3) собираем список заново в порядке сервера: совпавшие теги переиспользуем,
        //    изменившиеся мутируем на месте, новые берём как есть; удалённые отпадают сами
        const end = Math.min(job.cursor + BATCH, job.next.length);
        while (job.cursor < end) {
          const nextTag = job.next[job.cursor];
          const prevTag = job.byId.get(nextTag.Id);
          if (!prevTag) {
            job.result?.push(nextTag);
          } else {
            if (!sameTag(prevTag, nextTag)) {
              applyTag(prevTag, nextTag);
            }
            job.result?.push(prevTag);
          }
          job.cursor += 1;
        }
        if (job.cursor >= job.next.length) {
          jobIndex += 1;
        }
      } while (hasTime());
      return false;
    };

    const guardedStep = (hasTime: () => boolean): boolean => {
      try {
        return step(hasTime);
      } catch (e) {
        abort(e);
        return true;
      }
    };

    const stop = runIdleChunks(guardedStep);
    entry.cancel = () => {
      stop();
      entry.cancel = null;
      resolve();
    };
  });
}

function fetchAndApply(type: number, entry: CacheEntry): Promise<void> {
  return TagsApi.getTagList(type)
    .then((data) => {
      // предыдущая сверка, если ещё идёт, больше не актуальна
      if (entry.cancel) {
        entry.cancel();
      }
      if (!entry.loaded) {
        entry.container.value = buildContainer(data);
        entry.loaded = true;
        return undefined;
      }
      return mergeContainer(entry, data);
    })
    .catch((e) => {
      console.log(e);
    });
}

function startFetch(type: number, entry: CacheEntry): Promise<void> {
  const run = fetchAndApply(type, entry).then(() => {
    entry.inflight = null;
    if (!entry.dirty) {
      return undefined;
    }
    // теги меняли, пока ответ был в пути, — этот ответ мог их не застать
    entry.dirty = false;
    return startFetch(type, entry);
  });
  entry.inflight = run;
  return run;
}

function ensureFresh(type: number, entry: CacheEntry): Promise<void> {
  if (entry.inflight) {
    return entry.inflight;
  }
  return startFetch(type, entry);
}

/**
 * Сбросить кеш тегов сущности после их изменения. Вызывается из TagsApi после
 * добавления/открепления тега. Если дропдаун этого типа сейчас не открыт, ничего
 * не делаем: при следующем открытии список и так перезапрашивается.
 */
export function revalidateTags(entityType: number): Promise<void> {
  const entry = cache.get(entityType);
  if (!entry || entry.subscribers === 0) {
    return Promise.resolve();
  }
  if (entry.inflight) {
    entry.dirty = true;
    return entry.inflight;
  }
  return startFetch(entityType, entry);
}

/** Полная очистка — при выходе из аккаунта, чтобы не показать чужие теги. */
export function clearTagsCache(): void {
  for (const entry of cache.values()) {
    if (entry.cancel) {
      entry.cancel();
    }
  }
  cache.clear();
}

export type TagListHandle = {
  container: ComputedRef<TagContainer>;
  loading: Ref<boolean>;
};

/**
 * Список тегов для типа сущности: отдаёт кеш сразу, если он есть, и сам держит
 * его свежим. Лоадер поднимается только когда показывать пока нечего.
 */
export function useTagList(entityType: Ref<TagEntityType | undefined>): TagListHandle {
  const activeEntry = shallowRef<CacheEntry | null>(null);
  const loading = ref(false);

  const container = computed<TagContainer>(() => {
    const entry = activeEntry.value;
    if (!entry) {
      return EMPTY_CONTAINER;
    }
    return entry.container.value;
  });

  let attached: CacheEntry | null = null;

  const detach = () => {
    if (!attached) {
      return;
    }
    attached.subscribers -= 1;
    attached = null;
    activeEntry.value = null;
  };

  const attach = (type: TagEntityType | undefined) => {
    detach();
    if (type == null) {
      loading.value = false;
      return;
    }
    const entry = getEntry(type);
    entry.subscribers += 1;
    attached = entry;
    activeEntry.value = entry;
    loading.value = !entry.loaded;
    void ensureFresh(type, entry).then(() => {
      if (attached === entry) {
        loading.value = false;
      }
    });
  };

  watch(entityType, (value) => attach(value), { immediate: true });
  onUnmounted(detach);

  return { container, loading };
}
